import type { ServitutRow } from "../model/row";
import {
   selectDocumentSourceKind,
   selectIdentityLabel,
   selectIsFileOrigin,
   selectRowAktId,
   selectRowAktSource,
   selectWorkingDocumentLabel,
} from "../store/selectors";

/**
 * Read-only flatten for presentational matrix/export code.
 * Pill/name fields come from selectors so A and B cannot disagree.
 */
export function toMatrixView(row: ServitutRow) {
   const document = row.document;
   const fileOrigin = selectIsFileOrigin(row);
   const workingDocumentLabel = selectWorkingDocumentLabel(row);
   const fileBinding = document.kind === "upload"
      ? { s3Key: document.s3Key, filename: document.filename }
      : document.kind === "annotation" && fileOrigin
         ? { s3Key: document.s3Key, filename: document.filename || workingDocumentLabel }
         : undefined;
   return {
      key: row.id,
      id: row.id,
      servitut: row.landRegistry.servitut,
      handling: row.landRegistry.handling,
      displayAktId: row.landRegistry.displayAktId,
      datoLoebenr: selectIdentityLabel(row),
      identityLabel: selectIdentityLabel(row),
      workingDocumentLabel,
      fileOrigin,
      priority: row.landRegistry.priority,
      summary: row.landRegistry.summary,
      analysisAktId: row.identity.aktId || selectRowAktId(row) || row.id,
      includedBfes: row.landRegistry.includedBfes,
      pdfStatus: row.pdfStatus,
      pdfSource: selectRowAktSource(row),
      aktDocument: workingDocumentLabel,
      aktId: fileOrigin ? undefined : selectRowAktId(row),
      documentUrl: document.kind === "tingbog_akt" ? document.url : undefined,
      analysisItem: row.analysis?.item,
      selectedPages: row.pages.selected,
      exportFileName: workingDocumentLabel,
      documentSource: selectDocumentSourceKind(row),
      customPdf: fileBinding,
      annotationRecord: document.kind === "annotation" ? { id: document.recordId, document: { s3Key: document.s3Key, extractedPages: document.extractedAktPages, filename: document.filename, s3Url: document.s3Url, mimeType: document.mimeType, size: document.size } } : undefined,
      annotationDocument: document.kind === "annotation"
         ? { s3Key: document.s3Key, extractedPages: document.extractedAktPages, filename: document.filename, s3Url: document.s3Url, mimeType: document.mimeType, size: document.size }
         : undefined,
      electronicServitut: document.kind === "tingbog_digital"
         ? document.electronicServitut
         : row.registryBinding?.kind === "tingbog_digital"
            ? row.registryBinding.electronicServitut
            : undefined,
      sourceMessage: row.sourceMessage,
      provenance: {
         reviewState: row.review.state,
         origin: row.review.origin,
         source: fileOrigin ? "file" as const : "akt" as const,
         aiPagesKept: row.pages.aiSuggested,
         analysisStale: row.analysis?.stale,
         fieldStatus: {
            resume: row.fields.resume.aiStatus,
            entitledParty: row.fields.entitledParty.aiStatus,
            condition: row.fields.condition.aiStatus,
            statement: row.fields.statement.aiStatus,
            pages: row.review.state === "checked" ? "person_approved" as const : undefined,
         },
      },
      fieldAnnotations: {
         resume: row.fields.resume.acceptedNoteId
            ? { id: row.fields.resume.acceptedNoteId, verified: true, note: { comment: "", text: row.fields.resume.acceptedText || "" } }
            : undefined,
         entitledParty: row.fields.entitledParty.acceptedNoteId
            ? { id: row.fields.entitledParty.acceptedNoteId, verified: true, note: { comment: "", text: row.fields.entitledParty.acceptedText || "" } }
            : undefined,
         condition: row.fields.condition.acceptedNoteId
            ? { id: row.fields.condition.acceptedNoteId, verified: true, note: { comment: "", text: row.fields.condition.acceptedText || "" } }
            : undefined,
         statement: row.fields.statement.acceptedNoteId
            ? { id: row.fields.statement.acceptedNoteId, verified: true, note: { comment: "", text: row.fields.statement.acceptedText || "" } }
            : undefined,
      },
      stedfaestelseAnnotation: row.stedfaestelseId ? { id: row.stedfaestelseId } : undefined,
      job: row.job,
      fieldAiTexts: {
         resume: row.fields.resume.aiText,
         entitledParty: row.fields.entitledParty.aiText,
         condition: row.fields.condition.aiText,
         statement: row.fields.statement.aiText,
      },
      fieldOverridePending: row.fieldOverridePending,
      fieldOverrideStamp: row.fieldOverrideStamp,
   };
}

export type ServitutMatrixViewRow = ReturnType<typeof toMatrixView>;
